import { toast } from '@Shelly/shelly-design-system';
import { stringToHex } from './utilities';
import { includingDeviceData, zwavePendingAssociation } from 'src/stores/features/inclusion';
import { device_object } from 'src/stores/wizards';
import { devices } from 'src/stores';
import { get } from 'svelte/store';
import { is_gen_z, real_id } from './interface_helpers';
import type { Device } from 'src/types/Device';
import { devices_config_wave } from 'src/devices_config_wave';
import { addDeviceModal } from 'src/stores/device-runtime/firmware-ui';
import { gen2Request } from 'src/services/gen_2_http_reqs';
import { postReq } from 'src/services/http';
// import sha1 from 'crypto-js/sha1';
// import { user_data } from 'src/stores/account/user';
// import { get } from "svelte/store";

export enum SecurityClass {
	/**
	 * Used internally during inclusion of a node. Don't use this!
	 */
	Temporary = -2,
	/**
	 * `None` is used to indicate that a node is included without security.
	 * It is not meant as input to methods that accept a security class.
	 */
	None = -1,
	S2_Unauthenticated = 0,
	S2_Authenticated = 1,
	S2_AccessControl = 2,
	S0_Legacy = 7,
}
export type Either<T, Or> = T | Or;
export const NOT_KNOWN = undefined;
export type NOT_KNOWN = typeof NOT_KNOWN;
export type MaybeNotKnown<T> = Either<T, NOT_KNOWN>;

export type S2SecurityClass =
	| SecurityClass.S2_Unauthenticated
	| SecurityClass.S2_Authenticated
	| SecurityClass.S2_AccessControl;

/** Tests if the given security class is S2 */
export function securityClassIsS2(secClass: SecurityClass | undefined): secClass is S2SecurityClass {
	return (
		secClass != undefined &&
		secClass >= SecurityClass.S2_Unauthenticated &&
		secClass <= SecurityClass.S2_AccessControl
	);
}

/** An array of security classes, ordered from high (index 0) to low (index > 0) */
export const securityClassOrder = [
	SecurityClass.S2_AccessControl,
	SecurityClass.S2_Authenticated,
	SecurityClass.S2_Unauthenticated,
	SecurityClass.S0_Legacy,
] as const;

/** Cloud API path for legacy Z-Wave gateway commands (ss_add, nl_del_n, etc.). Prefer {@link xzRequest} for GZ end-device RPC. */
export const ZWAVE_DEVICE_API = '/device/zw';
/** Bulk HTTP path parallel to `/v2/devices/jrpc` for Z-Wave end devices. */
export const ZWAVE_XZ_BULK_API = '/v2/devices/xz';
export const formatGrantedSecurityClasses = (securityClasses: number[]): string => {
	return securityClassOrder
		.filter((securityClass) => securityClasses.includes(securityClass))
		.join(',');
};

export interface SecurityClassOwner {
	readonly id: number;
	getHighestSecurityClass(): MaybeNotKnown<SecurityClass>;
	hasSecurityClass(securityClass: SecurityClass): MaybeNotKnown<boolean>;
	setSecurityClass(securityClass: SecurityClass, granted: boolean): void;
}

/** Parses a bit mask into a numeric array */
export function parseBitMask(mask: Buffer, startValue: number = 1): number[] {
	const numBits = mask.length * 8;

	const ret: number[] = [];
	for (let index = 1; index <= numBits; index++) {
		const byteNum = (index - 1) >>> 3; // id / 8
		const bitNum = (index - 1) % 8;
		if ((mask[byteNum] & (2 ** bitNum)) !== 0) {
			ret.push(index + startValue - 1);
		}
	}
	return ret;
}

export function getHighestSecurityClass(securityClasses: SecurityClass[]): SecurityClass {
	for (const cls of securityClassOrder) {
		if (securityClasses.includes(cls)) return cls;
	}
	return SecurityClass.None;
}

function readNumber(qr: string, offset: number, length: number): number {
	return parseInt(qr.slice(offset, offset + length), 10);
}

function fail(reason: string): void {
	toast.open({ type: 'danger', title: `Invalid QR code: ${reason}` });
}

/** Reads a number between 0 and 99 (2 decimal digits) */
function readLevel(qr: string, offset: number): number {
	const ret = readNumber(qr, offset, 2);
	// console.warn(readNumber(qr, offset, 2));
	if (stringToHex(ret) > 99) fail('invalid data readLevel');
	return ret;
}

/** Reads a byte (3 decimal digits) */
function readUInt8(qr: string, offset: number): number {
	const ret = readNumber(qr, offset, 3);
	if (stringToHex(ret) > 0xff) fail('invalid data 8');
	return ret;
}

/** Reads a 2-byte value (5 decimal digits) */
function readUInt16(qr: string, offset: number): number {
	const ret = readNumber(qr, offset, 5);
	// console.warn('ret', ret);
	if (stringToHex(ret) > 0xffff) fail('invalid data 16');
	return ret;
}

const onlyDigitsRegex = /^\d+$/;
const minQRCodeLength = 52; // 2 digits Z, 2 digits version, 5 digits checksum, 3 digits keys, 40 digits DSK

export enum QRCodeVersion {
	S2 = 0,
	SmartStart = 1,
}

/** Digits-only DSK (40 chars) or dashed groups → `#####-#####-…-#####` (max 8 groups). */
export const formatDskToDashed = (raw: string): string => {
	const digits = String(raw || '')
		.replace(/\D/g, '')
		.slice(0, 40);
	if (!digits) return '';
	return (digits.match(/.{1,5}/g) ?? []).join('-');
};

/**
 * Validates a Z-Wave DSK (dashed or undashed). Returns the 8 numeric groups, or false.
 */
export function isValidDSK(dsk: string): number[] | false {
	const dashed = formatDskToDashed(dsk);
	if (!/^(\d{5}-){7}\d{5}$/.test(dashed)) return false;

	return dashed.split('-').map((p) => parseInt(p, 10));
}

const MANUAL_REQUESTED_SECURITY_CLASSES = [0, 1, 2, 7] as const;

export type ZWaveSecurityClassOption = {
	title: string;
	index: number;
	disabled: boolean;
};

export const buildValidateSecurityClasses = (
	requested: number[],
): Record<string, ZWaveSecurityClassOption> => ({
	'-2': {
		title: 'Temporary',
		index: -2,
		disabled: !requested.includes(-2),
	},
	'-1': {
		title: 'None',
		index: -1,
		disabled: !requested.includes(-1),
	},
	0: {
		title: 'Unauthenticated',
		index: 0,
		disabled: !requested.includes(0),
	},
	1: {
		title: 'Authenticated',
		index: 1,
		disabled: !requested.includes(1),
	},
	2: {
		title: 'AccessControl',
		index: 2,
		disabled: !requested.includes(2),
	},
	7: {
		title: 'Legacy',
		index: 7,
		disabled: !requested.includes(7),
	},
});

/**
 * Builds inclusion store payload for manual DSK entry (no QR TLVs).
 * Defaults granted security class to Authenticated (1); enables 0/1/2/7 for selection.
 */
export const buildManualDskParsedData = (rawDsk: string) => {
	const dsk = formatDskToDashed(rawDsk);
	if (!isValidDSK(dsk)) return undefined;

	const requestedSecurityClasses = [...MANUAL_REQUESTED_SECURITY_CLASSES];
	const securityClasses = [SecurityClass.S2_Authenticated];
	const parsedData = {
		dsk,
		requestedSecurityClasses,
		securityClasses,
		validateSecurityClasses: buildValidateSecurityClasses(requestedSecurityClasses),
		entryMode: 'dsk' as const,
	};

	logZWaveDsk('manual-dsk', {
		dsk,
		entryMode: 'dsk',
		securityClasses,
		requestedSecurityClasses,
	});
	includingDeviceData.updateData({ zWaveParsedData: parsedData });
	return parsedData;
};

export enum ProvisioningInformationType {
	ProductType = 0x00,
	ProductId = 0x01,
	MaxInclusionRequestInterval = 0x02,
	UUID16 = 0x03,
	SupportedProtocols = 0x04,
	// The ones below are NOT QR code compatible and therefore not implemented here
	Name = 0x32,
	Location = 0x33,
	SmartStartInclusionSetting = 0x34,
	AdvancedJoining = 0x35,
	BootstrappingMode = 0x36,
	NetworkStatus = 0x37,
}

export interface ProvisioningInformation_ProductType {
	genericDeviceClass: number;
	specificDeviceClass: number;
	installerIconType: number;
}

export interface ProvisioningInformation_ProductId {
	manufacturerId: number | string;
	productType: number | string;
	productId: number | string;
	applicationVersion: string;
}

export interface ProvisioningInformation_MaxInclusionRequestInterval {
	maxInclusionRequestInterval: number;
}

export interface ProvisioningInformation_UUID16 {
	uuid: string;
}

export interface ProvisioningInformation_SupportedProtocols {}

export type QRProvisioningInformation = {
	version: QRCodeVersion;
	/**
	 * The security classes that were **requested** by the device.
	 */
	readonly requestedSecurityClasses;
	/**
	 * The security classes that will be **granted** to this device.
	 * Until this has been changed by a user, this will be identical to {@link requestedSecurityClasses}.
	 */
	dsk: string;
} & ProvisioningInformation_ProductType &
	ProvisioningInformation_ProductId &
	Partial<ProvisioningInformation_MaxInclusionRequestInterval> &
	Partial<ProvisioningInformation_UUID16>;

// const getDeviceTypeLocationVersion = () => {
//Pako-all devices has 3 different types (EU, AU, US). I think this will solve the problem for now. This isn't needed for us but will leave it just in case
//     return get(user_data).timezone.toLowerCase().includes('eur') ? 'EU' : get(user_data).timezone.toLowerCase().includes('amer') ? 'US' : get(user_data).timezone.toLowerCase().includes('australia') ? 'AU' : 'EU';
// };

/** Maps Z-Wave product type + product ID (from QR TLV) to FE ZW-* model ids. */
const zWaveMappedDeviceTypes: Record<string, string> = {
	'0x0001_0x0083': 'ZW-DIMMER', // Wave Dimmer (EU/US/ANZ)
	'0x0002_0x0083': 'ZW-SWITCH', // Wave 1
	'0x0002_0x0084': 'ZW-SWITCH', // Wave 1 PM
	'0x0002_0x0081': 'ZW-SWITCH', // Wave 2 PM
	'0x0003_0x0082': 'ZW-COVER', // Wave Shutter
	'0x0003_0x0084': 'ZW-COVER', // Wave Pro Shutter
	'0x0009_0x0081': 'ZW-INPUT', // Wave I4
	'0x0009_0x0082': 'ZW-INPUT', // Wave I4 DC
	'0x0002_0x0089': 'ZW-SWITCH', // Wave Plug UK
	'0x0002_0x0088': 'ZW-SWITCH', // Wave Plug US
	'0x0002_0x008A': 'ZW-SWITCH', // Wave Pro 1
	'0x0002_0x008B': 'ZW-SWITCH', // Wave Pro 1 PM
	'0x0002_0x008C': 'ZW-SWITCH', // Wave Pro 2
	'0x0002_0x008D': 'ZW-SWITCH', // Wave Pro 2 PM
};

/**
 * Product type alone identifies the Wave family, so a product id we don't know yet
 * still resolves to the right ZW-* model instead of failing the whole QR payload.
 */
const ZWAVE_PRODUCT_TYPE_FAMILIES: Record<string, string> = {
	'0x0001': 'ZW-DIMMER',
	'0x0002': 'ZW-SWITCH',
	'0x0003': 'ZW-COVER',
	'0x0009': 'ZW-SENSOR',
};

const resolveZWaveDeviceType = (productType: string | number, productIdHex: string | number) => {
	return (
		zWaveMappedDeviceTypes[`${productType}_${productIdHex}`] ??
		ZWAVE_PRODUCT_TYPE_FAMILIES[`${productType}`]
	);
};

function parseTLVData(type: ProvisioningInformationType, data: string) {
	switch (type) {
		case ProvisioningInformationType.ProductType: {
			const deviceClasses = readUInt16(data, 0);
			const installerIconType = readUInt16(data, 5);
			const ret: ProvisioningInformation_ProductType = {
				genericDeviceClass: deviceClasses >>> 8,
				specificDeviceClass: deviceClasses & 0xff,
				installerIconType,
			};
			return ret;
		}

		case ProvisioningInformationType.ProductId: {
			const manufacturerId = stringToHex(readUInt16(data, 0));
			const productType = stringToHex(readUInt16(data, 5));
			const productIdHex = stringToHex(readUInt16(data, 10));
			const productId = resolveZWaveDeviceType(productType, productIdHex);
			if (!productId) {
				fail(`unknown product ${productType}/${productIdHex}`);
				return;
			}
			const applicationVersionNumeric = readUInt16(data, 15);
			const applicationVersion = `${applicationVersionNumeric >>> 8}.${applicationVersionNumeric & 0xff}`;
			const ret: ProvisioningInformation_ProductId = {
				manufacturerId,
				productType,
				productId,
				applicationVersion,
			};
			return ret;
		}

		case ProvisioningInformationType.MaxInclusionRequestInterval: {
			const maxInclusionRequestInterval = 128 * readLevel(data, 0);
			const ret: ProvisioningInformation_MaxInclusionRequestInterval = {
				maxInclusionRequestInterval,
			};
			return ret;
		}

		// case ProvisioningInformationType.UUID16: {
		//     // const buffer = Buffer.allocUnsafe(16);
		//     // Only format 0 is supported here
		//     const presentationFormat = readLevel(data, 0);
		//     if (presentationFormat !== 0) return;

		//     for (let chunk = 0; chunk < 8; chunk++) {
		//         const value = readUInt16(data, 2 + chunk * 5);
		//         // buffer.writeUInt16BE(value, chunk * 2);

		//     }
		//     const ret: ProvisioningInformation_UUID16 = {
		//         // uuid: buffer.toString("hex"),
		//     };
		//     return ret;
		// }

		// case ProvisioningInformationType.SupportedProtocols: {
		//     // const bitMask = Buffer.from([
		//     //     data.length === 2
		//     //         ? readLevel(data, 0)
		//     //         : data.length === 3
		//     //             ? readUInt8(data, 0)
		//     //             : data.length === 5
		//     //                 ? readUInt16(data, 0)
		//     //                 : 0,
		//     // ]);
		//     const ret: ProvisioningInformation_SupportedProtocols = {
		//         // bitMask
		//     };
		//     return ret;
		// }
	}
}

function parseTLV(qr: string): {
	entry: {
		type: ProvisioningInformationType;
	} & Record<string, any>;
	charsRead: number;
} {
	let offset = 0;
	if (qr.length - offset < 4) fail('incomplete TLV block');
	const typeCritical = readLevel(qr, offset);
	// console.warn('typecritical', typeCritical);
	const type = typeCritical >>> 1;
	const critical = !!(typeCritical & 0b1);
	// console.warn('critical', critical);
	const length = readLevel(qr, offset + 2);
	offset += 4;
	if (qr.length - offset < length) fail('incomplete TLV block');

	const data = qr.slice(offset, offset + length);
	offset += length;

	// Try to parse the raw data and fail if a critical block is not understood
	const parsed = parseTLVData(type, data);
	if (!parsed && critical) fail('Unsupported critical TLV block');

	let entry: any;
	if (parsed) {
		entry = {
			type,
			...parsed,
		};
	} else {
		entry = {
			type,
			[ProvisioningInformationType[type]]: data,
		};
	}
	return {
		entry,
		charsRead: offset,
	};
}
// function hex2a(hex) {
//     let _hex = hex.toString();//force conversion
//     let str = '';
//     for (let i = 0; i < _hex.length; i += 2) {
//         str += String.fromCharCode(parseInt(_hex.substr(i, 2)));
//         console.warn('str', str);
//     }
//     return str;
// }

/** Dev-only lifecycle logger for Z-Wave DSK (QR → provision → pending → match). */
export const logZWaveDsk = (stage: string, data: Record<string, unknown>): void => {
	if (!import.meta.env.DEV) return;
	console.log(`[zwave][dsk] ${stage}`, data);
};

/** Parses a string that has been decoded from a Z-Wave (S2 or SmartStart) QR code */
export function parseQRCodeString(qr: string) {
	//testing qr codes. comment them and will receive scanned code
	// qr = '900102501003370783399929866564055360422022269074375900100409601792022001120000020013102573';//shelly wave 1
	// qr = '9001240741314578411369255810538038577408006363616367001004096017920220011200000200129025870803001';//shelly wave 1

	// qr = '900130133003269615779236591108900189012133169833918700100409601792022001120000020013202573';//shelly SQW1PM
	// qr = '900130591003182734365123043251640103734899017705426200100409601792022001120000020013102573';//shelly SQW1
	if (qr.length === 0) fail('too short');

	// Validate the QR code
	// Trim off whitespace that might have been copied by accident
	qr.trim()
		.split('')
		.map((s) => +s);
	if (!qr.startsWith('90')) fail('must start with 90');
	if (qr.length < minQRCodeLength) fail('too short');
	if (!onlyDigitsRegex.test(qr)) fail('contains invalid characters');

	const version = readLevel(qr, 2);

	if (version > QRCodeVersion.SmartStart) fail('invalid version');

	const requestedKeysBitmask = readUInt8(qr, 9);

	const BufferedRequestedKeys = Array.from([requestedKeysBitmask]);
	const requestedSecurityClasses = parseBitMask(
		BufferedRequestedKeys,
		SecurityClass.S2_Unauthenticated,
	);
	// if (!requestedSecurityClasses.every((k) => k in SecurityClass)) {
	//     fail("invalid security class requested");
	// }

	let offset = 12;
	// const dsk = Buffer.allocUnsafe(16);
	for (let dskBlock = 0; dskBlock < 8; dskBlock++) {
		const block = readUInt16(qr, offset);
		// dsk.writeUInt16BE(block, dskBlock * 2);
		offset += 5;
	}

	const ret = {
		version,
		requestedSecurityClasses,
		securityClasses: [...requestedSecurityClasses],
		dsk: '',
	} as QRProvisioningInformation;
	let parsedData: {
		productId: string | number;
		applicationVersion: string;
		dsk: string;
		genericDeviceClass: number;
		installerIconType: number;
		manufacturerId: string | number;
		productType: string | number;
		requestedSecurityClasses: number[];
		specificDeviceClass: number;
		securityClasses: [];
		validateSecurityClasses: {};
		qr: string;
	} = undefined;
	let hasProductID = false;
	let hasProductType = false;

	while (offset < qr.length) {
		const {
			entry: { type, ...data },
			charsRead,
		} = parseTLV(qr.slice(offset));
		offset += charsRead;
		if (type === ProvisioningInformationType.ProductId) {
			hasProductID = true;
		} else if (type === ProvisioningInformationType.ProductType) {
			hasProductType = true;
		}
		parsedData = { ...Object.assign(ret, data), qr };
	}
	//ToDo finish output data
	//Create homeData entry
	//Get end point(next week)
	//create store with connecting devices(and clear them after connection)
	// Ensure the required fields are present
	if (!hasProductID || !hasProductType) {
		fail('missing required fields');
	}
	parsedData.validateSecurityClasses = buildValidateSecurityClasses(
		parsedData.requestedSecurityClasses ?? parsedData.securityClasses ?? [],
	);
	//ToDo-need device ID from cloud when node joins
	const dskUndashed = qr.substring(12, 52);
	const dsk = dskUndashed.replace(/(.{5})/g, '$1-');
	parsedData.dsk = dsk.slice(0, dsk.length - 1);
	logZWaveDsk('qr-parsed', {
		qr,
		qrLength: qr.length,
		dskUndashed,
		dsk: parsedData.dsk,
		productId: parsedData.productId,
		productType: parsedData.productType,
		requestedSecurityClasses: parsedData.requestedSecurityClasses,
		securityClasses: parsedData.securityClasses,
		malformedDsk: parsedData.dsk.length !== 47 || qr.length < minQRCodeLength,
	});
	includingDeviceData.updateData({ zWaveParsedData: parsedData });
	return parsedData;
}

type ZWaveProvisionParsedData = {
	qr?: string;
	dsk: string;
	securityClasses: number[];
};

/** Gen2 RPC — SmartStart provisioning entry on the Wave gateway. */
export const provisionSmartStartNode = async (
	gateway: Device,
	zWaveParsedData: ZWaveProvisionParsedData,
	longRange = false,
) => {
	const securityClasses =
		zWaveParsedData.securityClasses?.length > 0 ? zWaveParsedData.securityClasses : [1];

	const params = zWaveParsedData.qr
		? {
				qr: zWaveParsedData.qr,
				long_range: longRange,
				security_classes: securityClasses,
			}
		: {
				dsk: zWaveParsedData.dsk,
				long_range: longRange,
				security_classes: securityClasses,
			};

	logZWaveDsk('provision-request', {
		dsk: zWaveParsedData.dsk,
		qr: zWaveParsedData.qr,
		security_classes: securityClasses,
		long_range: longRange,
		gatewayId: gateway?.id,
		entryMode: zWaveParsedData.qr ? 'qr' : 'dsk',
	});

	return gen2Request(gateway, 'Zwave.ProvisionSmartStartNode', params, undefined, false, 60000);
};

export type ZWaveStartInclusionParams = {
	pin?: string;
	strategy?: 'secure' | 's2' | 'Security_S2';
};

/** Gen2 RPC — classic 30s inclusion window (manual pairing, no QR). */
export const startZWaveInclusion = async (gateway: Device, params: ZWaveStartInclusionParams = {}) => {
	return gen2Request(gateway, 'Zwave.StartInclusion', params, undefined, false, 60000);
};

/** Gen2 RPC — classic 30s exclusion window. */
export const startZWaveExclusion = async (gateway: Device) => {
	return gen2Request(gateway, 'Zwave.StartExclusion', {}, undefined, false, 60000);
};

/** Gen2 RPC — cancel running inclusion/exclusion window. */
export const stopZWaveLearnMode = async (gateway: Device) => {
	return gen2Request(gateway, 'Zwave.StopLearnMode', {}, undefined, false, 60000);
};

/** Gen2 RPC — gateway Z-Wave controller status (pairing window, etc.). */
export const getZWaveGatewayStatus = async (gateway: Device) => {
	return gen2Request(gateway, 'Zwave.GetStatus', {}, undefined, false, 10000);
};

const renameZWaveDeviceOnCloud = async (joinedDevice: Device, name: string): Promise<void> => {
	const trimmedName = name.trim();
	if (!trimmedName) return;

	const current = get(devices)[joinedDevice.id] || joinedDevice;
	const payload = { ...current, name: trimmedName };
	const response = await postReq('interface/device/save', {
		id: joinedDevice.id,
		data: JSON.stringify(payload),
	});

	if (!response?.isok) {
		logZWaveDsk('rename-failed', { deviceId: joinedDevice.id, name: trimmedName, response });
	}
};

const normalizeDsk = (dsk: string): string => dsk.replace(/-/g, '');

/** Find a cloud Z-Wave device by DSK (hyphens optional). */
export const findZWaveDeviceByDsk = (dsk: string): Device | undefined => {
	const targetDsk = normalizeDsk(dsk);
	const matched = Object.values(get(devices)).find((device) => {
		if (!is_gen_z(device)) return false;
		const deviceDsk = (device as Device & { dsk?: string }).dsk;
		return typeof deviceDsk === 'string' && normalizeDsk(deviceDsk) === targetDsk;
	});
	logZWaveDsk('find-by-dsk', {
		targetDsk,
		matched: Boolean(matched),
		matchedDeviceId: matched?.id,
	});
	return matched;
};

export type ZWaveAssociateData = {
	id: string;
	productId: string;
	dsk: string;
};

/** Open AssociateWizard for a Z-Wave device using its real cloud id (legacy / manual). */
export const handleAddzWaveDevice = ({ id, productId, dsk }: ZWaveAssociateData) => {
	const waveConfig = devices_config_wave[productId];
	const mode = waveConfig ? Object.keys(waveConfig.modes)[0] : undefined;
	const image = `images/device_images/${productId}.png`;
	const _device = {
		id,
		type: productId,
		category: waveConfig?.family,
		dsk,
		position: -1,
		gen: 'GZ',
		channel: 0,
		channels_count: (mode && waveConfig?.modes[mode]?.channels) || 1,
		mode: mode || '',
		name: undefined,
		room_id: undefined,
		image,
		cloud_options: { exclude_event_log: false },
		bundle: false,
		ip: undefined,
	} as Device;

	device_object.reset();
	device_object.set({
		..._device,
		enableCloudAfterAccountAssociation: true,
	});
	addDeviceModal.set(true);
};

/**
 * Clear pending SmartStart wait when BE adds the GZ device to the account.
 * BE places it in the gateway room — no AssociateWizard.
 * If the payload includes `dsk`, it must match the pending QR DSK; if omitted, any
 * pending GZ add completes the wait (single active inclusion).
 */
export const completeZWaveAssociationIfPending = (device: Device): boolean => {
	const pending = get(zwavePendingAssociation);
	const deviceDsk = (device as Device & { dsk?: string }).dsk;
	const pendingNormalized = typeof pending?.dsk === 'string' ? normalizeDsk(pending.dsk) : undefined;
	const deviceNormalized = typeof deviceDsk === 'string' ? normalizeDsk(deviceDsk) : undefined;

	if (!pending) {
		logZWaveDsk('association-check', {
			outcome: 'no-pending',
			deviceId: device?.id,
			deviceDsk,
		});
		return false;
	}

	if (!is_gen_z(device)) {
		logZWaveDsk('association-check', {
			outcome: 'not-gen-z',
			deviceId: device?.id,
			deviceDsk,
			pendingDsk: pending.dsk,
			pendingNormalized,
			deviceNormalized,
		});
		return false;
	}

	if (pending.gatewayId) {
		const gatewayDevice = get(devices)[pending.gatewayId];
		if (gatewayDevice && device.room_id !== gatewayDevice.room_id) {
			logZWaveDsk('association-check', {
				outcome: 'room-mismatch',
				deviceId: device.id,
				gatewayId: pending.gatewayId,
				deviceRoomId: device.room_id,
				gatewayRoomId: gatewayDevice.room_id,
			});
			return false;
		}
	}

	if (
		typeof pendingNormalized === 'string' &&
		typeof deviceNormalized === 'string' &&
		deviceNormalized !== pendingNormalized
	) {
		logZWaveDsk('association-check', {
			outcome: 'mismatch',
			deviceId: device.id,
			deviceDsk,
			pendingDsk: pending.dsk,
			pendingNormalized,
			deviceNormalized,
		});
		return false;
	}

	logZWaveDsk('association-check', {
		outcome: 'matched',
		deviceId: device.id,
		deviceDsk,
		pendingDsk: pending.dsk,
		pendingNormalized,
		deviceNormalized,
	});

	const pendingName = pending.name;
	zwavePendingAssociation.set(undefined);

	const customName = typeof pendingName === 'string' ? pendingName.trim() : '';
	if (customName) {
		void renameZWaveDeviceOnCloud(device, customName);
		const current = get(devices)[device.id];
		if (current) {
			devices.update({ ...current, name: customName });
		}
	}

	return true;
};
